// OpeningTimes.js
import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { DataContext } from '../Components/DataContext';
import CustomButton from './Button';
import WavyLine from '../Components/wavyline';

const OpeningTimes = () => {
  const data = useContext(DataContext);

  if (!data) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { operatingTimes } = data;

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <div className="absolute inset-0 top-1/2 overflow-hidden -z-10">
        <WavyLine />
      </div>
      
      <motion.h1 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-4xl font-bold text-secondary mb-8 text-center"
      >
        Operating Times
      </motion.h1>
      
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="p-8 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg transition-transform z-10 hover:shadow-xl"
        >
          <ul className="text-lg space-y-4">
            {Object.entries(operatingTimes).map(([day, time], index) => (
              <motion.li 
                key={day}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex justify-between items-center border-b border-gray-100 pb-3 hover:bg-gray-50 p-2 rounded-lg transition-colors"
              >
                <span className="font-medium text-gray-800">{day}</span>
                <span className="text-secondary font-semibold">{time}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
        
        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="md:absolute md:right-0 w-full md:w-[65%] h-full bg-gradient-to-br from-primary/90 to-secondary/90 backdrop-blur-sm rounded-2xl shadow-lg"
        >
          <div className="flex flex-col justify-center items-center h-full p-8 md:pl-24">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-2xl font-semibold text-white mb-4 text-center"
            >
              Ready to Visit?
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-white/90 mb-6 text-lg text-center max-w-[75%]"
            >
              Book your consultation today and take the first step towards your appointment.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <CustomButton
                to="/ConsultationForm"
                title="Get In Touch"
                accessibilityLabel="Get In Touch"
                className="bg-white text-primary px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OpeningTimes;